export default customElements.define('w-navigation', class Navigation extends wealthify.CustomElementBaseClass {

    constructor() {
        const styleSelector = 'c-navigation';
        const config = {
            styleSelector,
            templateHTML: /*html*/`
                <nav aria-label='Main navigation' class="${styleSelector}__wrapper" data-root>
                    <button class="${styleSelector}__mobile-menu-button u-display__none" aria-label="Open main navigation menu" data-mobile-nav-button>
                        <w-icon class="${styleSelector}__mobile-menu-button-icon" type="menu" data-mobile-nav-menu-icon></w-icon>
                    </button>
                    <div class="${styleSelector}__container ${styleSelector}__container--level-0" data-main-list-container>
                        <ul data-root-list class="${styleSelector}__list" data-main-list></ul>
                        <div class="${styleSelector}__mobile-account-buttons u-display__none--desktop" data-account-mobile-button-container>
                            <w-button-2 label="Join now" type="primary" size="small" data-account-mobile-button-invest></w-button-2>
                            <a class="u-display__none ${styleSelector}__mobile-account-link" data-account-mobile-button-unauth>Sign in</a>
                        </div>
                    </div>

                    <template data-nested-list-template>
                        <div class="${styleSelector}__container u-display__none" data-nested-list-container>
                            <ul class="${styleSelector}__list ${styleSelector}__list--nested">
                                <li class="${styleSelector}__list-item">
                                    <button class="${styleSelector}__list-action ${styleSelector}__list-action--mobile-back-button u-display__none--desktop" data-mobile-back-button>
                                        <w-icon type='caret-back'></w-icon>
                                        <span class="u-align__text-with-line-height"></span>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </template>
                    <template data-list-item-template><li class="${styleSelector}__list-item"></li></template>
                    <template data-link-template><a class="${styleSelector}__list-action" href=''></a></template>
                    <template data-button-template>
                        <button class="${styleSelector}__list-action" aria-expanded='false' data-is-expanded='false'></button>
                    </template>
                    <template data-button-component-template>
                        <w-button-2 class="${styleSelector}__list-action-button-component" size="small"></w-button-2>
                    </template>
                </nav>`,
            cacheDom: ['data-main-list-container', 'data-main-list', 'data-account-mobile-button-container', 'data-account-mobile-button-invest', 'data-account-mobile-button-unauth', 'data-button-template', 'data-button-component-template', 'data-link-template', 'data-nested-list-template', 'data-list-item-template', 'data-root-list', 'data-mobile-nav-button', 'data-mobile-nav-menu-icon']
        };

        super(config);

        this.onConnectedCallback = () => {
            this.classList.add(this.styleSelector);
            this.dataset.level = 0;
            this.#buildNavStructure();
            this.#setDefaultMobileLinks();

            this.addEventListener('keydown', this.#handleNavFocusByKeypress);
            this.dom.mobileNavButton.addEventListener('click', this.#handleMobileNavChange.bind(this));
        };
    }

    // public

    static arrowKeys = Object.freeze({
        LeftArrow: 37,
        UpArrow: 38,
        RightArrow: 39,
        DownArrow: 40,
    });

    static get observedProps () {
        return [
            new wealthify.Prop('structure', {type: wealthify.propTypes.Object()}),
            new wealthify.Prop('authenticated', {
                default: false,
                type: wealthify.propTypes.Boolean()
            }),
            // TODO: 03/02/2023, remove temporary theme prop and use global theming solution
            new wealthify.Prop('theme', {
                type: wealthify.propTypes.ArrayItem(Object.values(wealthify.enums.themes)),
                default: wealthify.enums.Dark
            })
        ];
    }

    get propCallbacks() {
        return {
            structure: () => this.#buildNavStructure(),
            authenticated: () => this.#toggleAuthenticatedView(),
            theme: () => {
                Object.values(wealthify.enums.themes).forEach((theme) =>
                    this.classList.remove(`${this.styleSelector}--${theme}-theme`)
                );

                this.classList.add(`${this.styleSelector}--theme-${this.theme}`);
            }
        };
    }

    // private

    #active = false;
    #built = false;

    #buildNavStructure() {
        if (!this.structure) {
            this.dom.mobileNavButton.classList.add('u-display__none');
            this.dom.mobileNavButton.classList.remove('u-display__none--desktop');

            return;
        } else {
            this.dom.mobileNavButton.classList.remove('u-display__none');
            this.dom.mobileNavButton.classList.add('u-display__none--desktop');
        }

        const fragment = document.createDocumentFragment();
        const navItems = this.#getNavList(this.structure);

        fragment.appendChild(navItems);

        this.#built = true;
        this.#toggleAuthenticatedView();
        this.dom.mainList.replaceChildren(fragment);
    }

    #getNavList (navData, level = 0) {
        // Level 0 has nav items but no label,
        // Therefore for level 0 we use base array, otherwise we pass entire level object
        const isRootLevel = level === 0;
        const navItems = isRootLevel ? navData : navData.action;

        // If level 0, return li elements otherwise return ul element
        const listContainerElement = isRootLevel
            ? document.createDocumentFragment()
            : this.dom.nestedListTemplate.content.cloneNode(true).firstElementChild;
        const ulElement = isRootLevel ? listContainerElement : listContainerElement.querySelector('ul');
        const listId = `list-${wealthify.convert.toKebabCase(navData.label ?? 'root')}`;

        if (!isRootLevel){
            const backButton = ulElement.querySelector('[data-mobile-back-button]');
            const backTextElement = backButton.querySelector('span');

            listContainerElement.id = listId;

            backButton.dataset.listElementId = listId;
            backButton.dataset.level = level - 1;
            backButton.addEventListener('click', this.#handleNavMenuChange.bind(this));
            backTextElement.textContent = navData.label;
        }

        navItems.forEach((navItem) => {
            ulElement.appendChild(this.#getNavListItem(navItem, level));
        });

        return listContainerElement;
    }

    #getNavListItem (navItem, level) {
        const liElement = this.dom.listItemTemplate.content.cloneNode(true).firstElementChild;
        const actionElements = [];
        const isLink = typeof navItem.action === 'string';

        let nestedList = null;

        if (navItem.styleModifiers) {
            navItem.styleModifiers.forEach((styleModifier) => liElement.classList.add(`${this.styleSelector}__list-item--${styleModifier}`));
        }

        if (navItem.utilityClasses) {
            navItem.utilityClasses.forEach((utilityClass) => liElement.classList.add(utilityClass));
        }

        actionElements.push(this.#getNavActionElement(navItem, level));

        if (navItem.useButtonType) {
            // If button component is used for action element
            // A normal version needs to be created for mobile

            actionElements[0].hidemobile = actionElements[0].hidetablet = true;
            delete navItem.useButtonType;
            actionElements.push(this.#getNavActionElement(navItem, level));

            actionElements[1].classList.add('u-display__none--desktop');
        }

        actionElements.forEach((actionElement) => liElement.appendChild(actionElement));

        if (!isLink) {
            nestedList = this.#getNavNestedList(navItem, level);
            actionElements.forEach((actionElement) => {
                actionElement.setAttribute('aria-controls', nestedList.id);
                actionElement.dataset.listElementId = nestedList.id;
            });
            liElement.appendChild(nestedList);
        }

        return liElement;
    }

    #getNavActionElement (navItem, level) {
        const isLink = typeof navItem.action === 'string';
        const isRootLevel = level === 0;

        let actionElement;

        if (navItem.useButtonType) {
            actionElement = this.dom.buttonComponentTemplate.content.cloneNode(true).firstElementChild;

            actionElement.label = navItem.label;
            actionElement.variant = navItem.useButtonType;
        } else {
            actionElement = isLink
                ? this.dom.linkTemplate.content.cloneNode(true).firstElementChild
                : this.dom.buttonTemplate.content.cloneNode(true).firstElementChild;

            actionElement.textContent = navItem.label;
        }

        if (navItem.icon) {
            actionElement.style.setProperty('--nav-list-action-background-image', `url(${navItem.icon})`);
            actionElement.classList.add(`${this.styleSelector}__list-action--has-icon`);
        }

        if (isLink){
            const attribute = navItem.useButtonType ? 'link' : 'href';

            actionElement[attribute] = navItem.action;
        } else {
            const caret = document.createElement('w-icon');

            actionElement.dataset.level = level + 1;
            actionElement.addEventListener('click', this.#handleNavMenuChange.bind(this));

            caret.type = 'caret-forward';
            caret.hidedesktop = isRootLevel;
            actionElement.appendChild(caret);
        }

        return actionElement;
    }

    #getNavNestedList (navItem, level) {
        const nextLevel = level + 1;
        const nestedList = this.#getNavList(navItem, nextLevel);
        const nestedClass = nextLevel === 1
            ? `${this.styleSelector}__container--nested`
            : `${this.styleSelector}__container--nested-deep`;

        nestedList.classList.add(nestedClass);
        nestedList.classList.add(`${this.styleSelector}__container--level-${nextLevel}`);

        return nestedList;
    }

    #handleNavMenuChange (event) {
        if (!this.#active) {
            this.#active = true;
            this.#toggleMobileNav();
            // Monitor click whilst nav is open
            document.addEventListener('click', this.#deactivateNavIfClickIsOutsideReference);
        }

        this.#toggleList.bind(this, event.currentTarget)();
    }

    #deactivateNavIfClickIsOutside(event) {
        if (!this.contains(event.target)) {
            this.#active = false;
            this.#toggleMobileNav();
            this.#toggleList();
            // Remove click monitoring
            document.removeEventListener('click', this.#deactivateNavIfClickIsOutsideReference);
        }
    }

    #deactivateNavIfClickIsOutsideReference = this.#deactivateNavIfClickIsOutside.bind(this);

    #handleNavFocusByKeypress (event) {
        const isBackwardAction = event.keyCode === this.constructor.arrowKeys.UpArrow || event.keyCode === this.constructor.arrowKeys.LeftArrow;
        const isForwardAction = event.keyCode === this.constructor.arrowKeys.DownArrow || event.keyCode === this.constructor.arrowKeys.RightArrow;

        if (isBackwardAction || isForwardAction){
            event.preventDefault();
            const listItemParent = document.activeElement.closest('li');
            const listParent = listItemParent.parentElement;
            const newElement = isBackwardAction ? listItemParent.previousElementSibling : listItemParent.nextElementSibling;
            const isNewElementBackButton = newElement?.firstElementChild.dataset.mobileBackButton !== undefined;

            let elementToBeFocussed;

            if (isNewElementBackButton || !newElement){
                // If on last element, skip to start and vice versa
                // If is forward action, select second child due to back button being first

                const oppositeBoundElement = isBackwardAction ? listParent.lastElementChild : listParent.children[1];

                elementToBeFocussed = oppositeBoundElement.firstElementChild;
            } else {
                elementToBeFocussed = newElement.firstElementChild;
            }

            elementToBeFocussed.focus();

            if (elementToBeFocussed !== document.activeElement) {
                // Focus hasn't worked due to next element being button component
                // Select inner button element
                elementToBeFocussed.firstElementChild.focus();
            }
        }
    }

    #handleMobileNavChange () {
        this.#active = !this.#active;
        this.#toggleMobileNav();

        document.body.style.overflow = this.#active ? 'hidden' : '';

        if (!this.#active) {
            this.#toggleList();
        }
    }

    #setDefaultMobileLinks () {
        this.dom.accountMobileButtonInvest.link = `${wealthify.settings.currentAppPath}CreatePlan`;
        this.dom.accountMobileButtonUnauth.href = `${wealthify.settings.currentAppPath}Auth`;
    }

    #toggleMobileNav () {
        const iconType = this.#active ? 'cross' : 'menu';
        const navMenuButtonAriaLabel = this.#active ? 'Close main navigation menu' : 'Open main navigation menu';

        this.#setActiveLevel(0);
        this.dom.mobileNavMenuIcon.type = iconType;
        this.dom.mobileNavButton.ariaLabel = navMenuButtonAriaLabel;
        this.dom.mainListContainer.classList.toggle('u-display__flex');
    }

    #toggleList (clickedButtonElement = this.querySelector('[data-is-expanded="true"]')) {
        if (!clickedButtonElement) {
            return;
        }

        const listElementId = clickedButtonElement.dataset.listElementId;
        const targetListContainerElement = this.querySelector(`#${listElementId}`);
        const parentListContainerElement = targetListContainerElement.closest('ul');
        const actionButtonElement = targetListContainerElement.closest('li').querySelector('w-button-2') ?? targetListContainerElement.closest('li').querySelector('button');
        const isToBeOpened = targetListContainerElement.classList.contains('u-display__none');
        const listElementToUpdate = isToBeOpened ? parentListContainerElement : targetListContainerElement;
        const listActionClass = actionButtonElement.tagName.toLowerCase() === 'button'
            ? '__list-action'
            : '__list-action-button-component';

        let hideClassListAction = isToBeOpened ? 'remove' : 'add',
            activeClassListAction = isToBeOpened ? 'add' : 'remove';

        if (isToBeOpened) {
            const expandedButtons = Array.from(listElementToUpdate.querySelectorAll('[data-is-expanded="true"]'));

            expandedButtons.filter(button => button !== clickedButtonElement).forEach(button => this.#toggleList(button));
        }

        targetListContainerElement.classList[hideClassListAction]('u-display__none');
        actionButtonElement.classList[activeClassListAction](`${this.styleSelector}${listActionClass}--active`);
        targetListContainerElement.classList[activeClassListAction](`${this.styleSelector}__container--active`);
        actionButtonElement.setAttribute('aria-expanded', isToBeOpened);
        actionButtonElement.dataset.isExpanded = isToBeOpened;

        this.#setActiveLevel(clickedButtonElement.dataset.level);
    }

    #setActiveLevel(level) {
        const currentLevelClass = Array.from(this.classList).filter((className) => className.includes('--active-level'))[0];

        if (currentLevelClass) {
            this.classList.remove(currentLevelClass);
        }

        this.dataset.level = level;
        this.dom.accountMobileButtonInvest.variant = parseInt(this.dataset.level) === 1 ? 'secondary' : 'primary';
        this.classList.add(`${this.styleSelector}--active-level-${this.dataset.level}`);
    }

    #toggleAuthenticatedView () {
        if (!this.#built) {
            return;
        }

        const unauthClassListAction = this.authenticated ? 'add' : 'remove';

        this.classList[unauthClassListAction](`${this.styleSelector}--authenticated`);
        this.dom.accountMobileButtonUnauth.classList[unauthClassListAction]('u-display__none');
    }
});